import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ApiClientService } from '@services/index';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AgreementAttachmentForm, FileUpload } from '../../shared-agreement.model';
import { Align, Permission } from 'app/enums';
import { FormGroup } from '@angular/forms';

@UntilDestroy()
@Component({
    selector: 'app-agreement-attachment',
    templateUrl: './agreement-attachment.component.html',
    styleUrl: './agreement-attachment.component.scss'
})
export class AgreementAttachmentComponent {
    @Input() public attachmentForm: FormGroup<AgreementAttachmentForm>;
    @Output() public fileDialogStateChange = new EventEmitter<boolean>();
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);

    public loadingIds: string[] = [];

    public readonly align = Align;
    public sortKey: keyof FileUpload = 'fileName';
    public sortAscending = true;
    public loading = false;
    public uploading = false;
    public pdfByteArray: ArrayBuffer;
    public permissions = Permission;
    public isDragOver = false;

    public get f() {
        return this.attachmentForm.controls;
    }

    public get disabled() {
        return this.f.attachments.disabled;
    }

    public get files() {
        return this.attachmentForm.controls.attachments.value;
    }

    public view(file: FileUpload) {
        if (file.fileExtension == 'pdf') {
            if (file.url) {
                this.apiClientService.downloadTempFile(file.url).subscribe((response) => {
                    this.pdfByteArray = new Uint8Array(response);
                    this.changeDetectorRef.markForCheck();
                });
            } else if (file.id) {
                this.apiClientService.download(file.id).subscribe((response) => {
                    this.pdfByteArray = new Uint8Array(response);
                    this.changeDetectorRef.markForCheck();
                });
            }
        }
    }

    public download(event: Event, file: FileUpload) {
        event.stopPropagation();
        if (this.loadingIds.includes(file.id)) {
            return;
        }
        this.loadingIds.push(file.id);

        if (file.url) {
            this.apiClientService.downloadTempFile(file.url).subscribe((response) => {
                this.downloadFile(file, response);
            });
        } else if (file.id) {
            this.apiClientService.download(file.id).subscribe((response) => {
                this.downloadFile(file, response);
            });
        }
    }

    private downloadFile(file: FileUpload, response: ArrayBuffer) {
        const blob = new Blob([response], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.fileName;
        link.click();

        window.URL.revokeObjectURL(downloadUrl); // Clean up
        this.loadingIds = this.loadingIds.filter((id) => id != file.id);
        this.changeDetectorRef.markForCheck();
    }

    // public onSortChange(sortKey: keyof AgreementFile, sortAscending = true) {
    //     //this.sortSubject.next({ key: sortKey, ascending: sortAscending });
    // }

    public resetFileUpload(event: Event) {
        this.fileDialogStateChange.emit(true);
        const element = event.currentTarget as HTMLInputElement;
        element.value = '';

        // Add focus listener to detect when dialog closes
        const onFocus = () => {
            setTimeout(() => {
                if (!element.files || element.files.length === 0) {
                    this.fileDialogStateChange.emit(false);
                }
            }, 100);
            window.removeEventListener('focus', onFocus);
        };
        window.addEventListener('focus', onFocus);
    }

    public uploadFile(event: Event) {
        this.uploading = true;
        const element = event.currentTarget as HTMLInputElement;
        if (element?.files && element.files.length > 0) {
            const file: File | null = element.files[0];
            this.handleFile(file);
        } else {
            // Dialog was canceled (no file selected)
            this.fileDialogStateChange.emit(false);
            this.uploading = false;
        }
    }

    public onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    public onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    public onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];
            this.handleFile(file);
        }
    }

    private handleFile(file: File) {
        if (!file) {
            this.uploading = false;
            this.fileDialogStateChange.emit(false);
            return;
        }

        const lastIndexOfDot = file.name.lastIndexOf('.');
        const fileExtension = file.name.slice(lastIndexOfDot + 1);

        this.apiClientService.upload(file).subscribe((response) => {
            const item = {
                fileName: file.name,
                fileExtension: fileExtension,
                url: response
            } as FileUpload;
            this.attachmentForm.value.attachments.push(item);
            this.f.attachments.markAsDirty();
            this.uploading = false;
            this.fileDialogStateChange.emit(false);
            this.changeDetectorRef.markForCheck();
        });
    }

    public deleteFile(item: FileUpload) {
        const index = this.files.indexOf(item);
        this.files.splice(index, 1);
        this.f.attachments.markAsDirty();
    }
}
